import {
    action,
    computed,
    IReactionDisposer,
    observable,
    set,
    toJS
} from 'mobx';
import { first, isEmpty, isNil, last } from 'lodash';
import colors from '@honeybook/hbui/dist/styles/colors';
import {
    IFlow,
    IFlowActionSummary,
    IFlowPublicConfiguration,
    IFlowTheme,
    IFlowThumbnail,
    LeadFormStatus,
    RevisionType,
    TActionBlocks,
    TemplateSourceType,
    TFlowStatus,
    TFlowTag,
    IFlowPermission
} from '~/types/model.types';
import type { RootStore } from '~/stores/root.store';
import type { RelatedFlowModel } from '~/models/related-flow.model';
import {
    IFixedFlowResponse,
    ILastContentEdit,
    IMember,
    IVariableResponse,
    IWorkflowResponse,
    RelationType,
    TBlockResponse
} from '~/types/server.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { BOOKABLE_BLOCK_TYPES } from '~/common/flows.constants';
import type { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import type { ContractBlock } from '~/models/blocks/contract-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import type { Page } from '~/models/page.model';
import { deleteContractRecaps } from '~/components/Builder/BuilderSidebar/ContractSidebar/ContractHtml.utils';
import type { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import type { ServicesBlock } from '~/models/blocks/services-block.model';
import { BaseFlow } from '~/models/base-flow.model';
import { ChargeContainerBlock } from '~/models/blocks/charge-container-block.model';
import { RecurringScheduleBlock } from '~/models/blocks/recurring-schedule-block.model';
import { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';
import { formatDate } from '~/utils/date.utils';
import { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import { QuestionType } from '~/types/builder.types';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import { TextBlock } from '~/models/blocks/text-block.model';
import { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import { ChargeBlock } from '~/models/blocks/charge-block.model';

export class Flow extends BaseFlow<RootStore> {
    rootStore: RootStore;

    @observable action_block_types: TActionBlocks[];
    @observable actions: IFlowActionSummary[] = [];
    @observable activeRevision: FlowRevision<RootStore>;
    @observable created_at: string;
    @observable draftRevision: FlowRevision<RootStore>;
    @observable first_viewed_at: string;
    @observable flow_url: string;
    @observable view_only_flow_url: string;
    @observable folder_id: string;
    @observable is_booked: boolean;
    @observable isBeingPublished: boolean;
    @observable last_action_number: number;
    @observable last_changed_at: string;
    @observable last_content_edit: ILastContentEdit;
    @observable last_viewed_at: any;
    @observable original_theme?: IFlowTheme;
    @observable path: string;
    @observable revision_count: number;
    @observable published_at: string;
    @observable published_by_user: { full_name: string };
    @observable related_flows: RelatedFlowModel[];
    @observable scheduled_to_be_sent: string;
    @observable starred: boolean;
    @observable flow_tags: TFlowTag[];
    @observable thumbnail_image: IFlowThumbnail;
    @observable viewed_by_member: boolean;
    @observable was_already_published: boolean;
    @observable wasMarkedAsPaidInCurrentSession: boolean = false;
    @observable source_attribution: TemplateSourceType;
    reactions: IReactionDisposer[];
    @observable linkedAutomations: IWorkflowResponse[];
    event_name: string;
    is_workspace_archived: boolean;
    owner_id: string;
    is_deletable: boolean;
    @observable public_configuration: IFlowPublicConfiguration;
    @observable source_flow_id: string;
    @observable flow_permissions: IFlowPermission[] = [];
    @observable require_strong_auth: boolean;

    constructor(flow: IFlow, rootStore: RootStore) {
        super(flow, rootStore);
        this.rootStore = rootStore;
        const { theme, variables, ...restOfFlow } = flow;
        set(this, restOfFlow);
        this.reactions = [];
    }

    @action
    setStatus(status: TFlowStatus) {
        this.status = status;
    }

    @action
    setEligibleForMarkAsCompleted(eligible: boolean) {
        this.is_eligible_for_mark_as_completed = eligible;
    }

    @computed
    get isTemplate() {
        return this.is_template;
    }

    @computed
    get project() {
        return this.rootStore.projectStore?.projects[this.project_id];
    }

    getProjectDate = () => {
        const startDate = this.project?.event_date;
        const endDate = this.project?.event_end_date;

        if (!startDate) {
            return 'TBD';
        }
        if (!endDate) {
            return formatDate(startDate, 'MM D, YYYY');
        }
        return `${formatDate(startDate, 'MM D, YYYY')} - ${formatDate(
            endDate,
            'MM D, YYYY'
        )}`;
    };

    getProjectTime = () => {
        const startTime = this.project?.event_time_start;
        const endTime = this.project?.event_time_end;

        if (!startTime) {
            return 'TBD';
        }
        if (!endTime) {
            return startTime;
        }
        return `${startTime} - ${endTime}`;
    };

    @computed
    get isPublished() {
        return this.is_published;
    }

    @computed
    get hasCriticalChanges() {
        return this.blocks.some(block => block.hasCriticalChanges);
    }

    @computed
    get hasContractCriticalChanges() {
        return this.contractBlocks.some(block => block.hasCriticalChanges);
    }

    @computed
    get sortedFlowRecipients() {
        return this.flowRecipients.sort((clientA, clientB) =>
            clientA.full_name?.localeCompare(clientB.full_name)
        );
    }

    @computed
    get hasInvoiceCriticalChanges() {
        return this.invoiceContainerBlock?.hasCriticalChanges;
    }

    @computed
    get hasServiceSelectionCriticalChanges() {
        return this.serviceSelectionBlocks.some(
            block => block.hasCriticalChanges
        );
    }

    @computed
    get hasLeadDetailsBlock() {
        return !!this.blocks?.find(
            block => block.type === 'lead_details_block'
        );
    }

    @computed
    get isBookable() {
        return this.blocks.some(block =>
            BOOKABLE_BLOCK_TYPES.includes(block.type)
        );
    }

    @computed
    get scheduleSend() {
        return this.scheduled_to_be_sent;
    }

    @computed
    get isAlreadyScheduledToBeSent() {
        return !!this.scheduleSend;
    }

    @computed
    get isInvoiceBeforePayment() {
        return (
            this.pageWithPay &&
            this.pageWithInvoice &&
            this.pageWithInvoice.order > this.pageWithPay.order
        );
    }

    @computed
    get paymentAfterScheduleGap() {
        return (
            this.pageWithPay &&
            this.pageWithScheduler &&
            this.pageWithPay.order - this.pageWithScheduler.order
        );
    }

    @computed
    get isContractRecapsBeforeSelection() {
        return this.contractBlocks.some(
            contract => contract.isBeforeSelection && contract.hasRecaps
        );
    }

    @computed
    get isInvoiceBeforeSelection() {
        const lastPageWithServiceSelection = last(
            this.sortedPagesWithServiceSelection
        );
        return (
            lastPageWithServiceSelection &&
            this.pageWithInvoice &&
            lastPageWithServiceSelection.order > this.pageWithInvoice.order
        );
    }

    @computed
    get isSchedulerBeforeSessionSelection() {
        return (
            this.pageWithScheduler?.order < this.pageWithSessionSelection?.order
        );
    }

    @computed
    get isPaymentOnLastPage() {
        return this.pageWithPay?.isLastPage;
    }

    @computed
    get isPublishedAndDirty() {
        return this.is_published && this.isDirty;
    }

    @action
    setTitle(title) {
        this.title = title;
    }

    // TODO: could probably be easily extracted to base-flow.model
    @action
    updateVariables(
        variables: IVariableResponse[],
        revisionType: RevisionType = RevisionType.Draft
    ) {
        const revision =
            revisionType === RevisionType.Active
                ? this.activeRevision
                : this.draftRevision;

        revision.updateVariables(variables);
    }

    @action
    updateBlocksWithConnectedVariableIds(variables) {
        this.draftRevision.updateBlocksWithConnectedVariableIds(variables);
    }

    @action
    updateBlocksWithDisconnectedVariableIds(variables) {
        this.draftRevision.updateBlocksWithDisconnectedVariableIds(variables);
    }

    @action
    setDirty(isDirty: boolean) {
        this.last_changed_at = isDirty
            ? new Date().toString()
            : this.published_at;
    }

    @action
    setIsBeingPublished(isBeingPublished: boolean) {
        this.isBeingPublished = isBeingPublished;
    }

    @action
    setScheduleSend(sendDate: string) {
        this.scheduled_to_be_sent = sendDate;
    }

    @action clearShareFlowPendingTask() {
        this.share_flow_pending_task_data = null;
    }

    @action.bound
    addMember(member: IMember) {
        if (
            this.members.some(
                existingMember => existingMember._id === member._id
            )
        ) {
            return;
        }

        this.members.push(member);
        this.workspace?.addMember(member); //no ws for template
    }

    @action.bound
    getMemberById(memberId) {
        return this.members.find(member => {
            return member._id === memberId;
        });
    }

    @action.bound
    relatedFlowsByType(type: RelationType) {
        return this?.related_flows?.filter(
            related => related.relation_type === type
        );
    }

    @action.bound
    latestRelatedFlow(relation_type: RelationType) {
        return this.relatedFlowsByType(relation_type).sort(
            (a, b) =>
                new Date(b.flow.created_at).getTime() -
                new Date(a.flow.created_at).getTime()
        )[0];
    }

    @computed
    get attachments() {
        return this.activeRevision?.variables
            .filter(
                variable =>
                    variable.type === 'upload_attachments_question_variable' &&
                    variable.raw_value !== null
            )
            .map(variable => variable.raw_value)
            .flat();
    }

    @action
    setPublishedAt(publishedAt: string) {
        this.published_at = publishedAt;
    }

    @computed
    get theme() {
        const brandColor =
            this.rootStore.companyStore?.currCompany?.brand_color;

        return {
            ...this._theme,
            styles: {
                ...this._theme.styles,
                accent_color:
                    this._theme.styles.accent_color ||
                    brandColor ||
                    colors.defaultBrandColor
            }
        };
    }

    @computed
    get themeColors() {
        const {
            styles: { accent_color, color, background_color },
            buttons_styles: { color: buttons_color },
            questions_styles: {
                background_color: questions_background_color,
                border_color: questions_border_color,
                warning_color: questions_warning_color,
                color: questions_color
            },
            heading_styles
        } = this.theme;

        return [
            background_color,
            color,
            accent_color,
            buttons_color,
            questions_color,
            questions_background_color,
            questions_warning_color,
            questions_border_color,
            heading_styles?.color
        ];
    }

    @computed
    get themeFonts() {
        return [
            this.theme.styles.font_family,
            this.theme.buttons_styles.font_family,
            this.theme.questions_styles.font_family,
            this.theme.heading_styles?.font_family
        ];
    }

    @computed
    get shareFlowPendingTaskData() {
        return this.share_flow_pending_task_data;
    }

    @computed
    get hasRecipients() {
        return this.flowRecipients.length > 0;
    }

    @computed
    get invoiceColumnHeaderBlock() {
        return this.blocks.find(
            blk => blk.type === 'invoice_column_header_block'
        ) as InvoiceColumnHeaderBlock;
    }

    @computed
    get invoiceSummaryBlock() {
        return this.blocks.find(
            blk => blk.type === 'invoice_summary_block'
        ) as InvoiceSummaryBlock;
    }

    @computed
    get invoiceHeaderBlock() {
        return this.blocks.find(
            block => block.type === 'invoice_header_block'
        ) as InvoiceHeaderBlock;
    }

    @computed
    get firstFlowVendor() {
        return this.flowVendorsList[0];
    }

    @computed
    get hasContract() {
        return this.action_block_types.includes('contract_block');
    }

    @computed
    get hasContractBlocks() {
        return this.contractBlocks.length > 0;
    }

    @computed
    get hasAnySignedContract() {
        return this.contractBlocks.some(
            contract => contract.hasClientSignatures
        );
    }

    @computed
    get firstFlowClient() {
        return this.flowRecipients[0];
    }

    @computed
    get flowRecipientsIds() {
        return this.flowRecipients.map(member => member._id);
    }

    @computed
    get flowRecipientsNames() {
        return this.flowRecipients.map(member => {
            return member.full_name;
        });
    }

    @computed
    get firstFlowRecipientFirstName() {
        return this.flowRecipients.map(member => {
            return member.first_name;
        })[0];
    }

    @computed
    get hasErrors() {
        return this.pages.some(page => page.hasErrors);
    }

    @computed
    get blocksWithErrors() {
        return this.pages.flatMap(page => page.blocksWithErrors);
    }

    @computed
    get linkedFields() {
        return this.pages
            .map(page => page.linkedFields)
            .reduce((allLinkedFields, pageLinkedFields) => {
                return allLinkedFields.concat(pageLinkedFields);
            });
    }

    @computed
    get projectName() {
        return this.project?.event_name || this.event_name || '';
    }

    @computed
    get isWorkspaceArchived() {
        return this.is_workspace_archived;
    }

    @computed
    get workspace() {
        return this.rootStore.workspaceStore.workspaces[this.workspace_id];
    }

    @computed
    get defaultEmailTemplate() {
        try {
            return this.company?.getEmailTemplate(
                this.configuration.default_email_template_id
            );
        } catch (e) {
            return null;
        }
    }

    @computed
    get customCTAText() {
        return this.configuration?.cta_button_text || '';
    }

    @computed
    get isLogoEnabled() {
        return this.configuration?.is_logo_enabled;
    }

    @computed
    get isDirty() {
        return (
            this.publicStatus !== LeadFormStatus.Draft &&
            (this.last_changed_at > this.published_at ||
                isNil(this.published_at))
        );
    }

    @computed
    get isFromPublicFlow() {
        return this.source_attribution === TemplateSourceType.PublicFlow;
    }

    @computed
    get autoPayClientName() {
        return this.flowRecipients.find(member => {
            return (
                this.chargeBlock?.connectedVariable?.value?.auto_payment
                    ?.activated_by === member._id
            );
        })?.full_name;
    }

    @computed
    get hasScheduleBlock() {
        return !!this.scheduleContainerBlock;
    }

    @computed
    get scheduleContainerBlock() {
        return this.blocks.find(
            b => b.isScheduleContainerBlock
        ) as ScheduleContainerBlock;
    }

    @computed
    get invoiceContainerBlock() {
        return this.blocks.find(b => b.isInvoiceContainerBlock) as InvoiceBlock;
    }

    @computed
    get uploadQuestionBlocks() {
        return this.questionBlocks.filter(
            block => block.question.question_type === QuestionType.Upload
        );
    }

    @computed
    get uploadQuestionBlocksCount() {
        return this.uploadQuestionBlocks.length;
    }

    @computed
    get hasInvoiceBlock() {
        return !!this.invoiceContainerBlock;
    }

    @computed
    get isOverlayShownForInvoice() {
        return (
            !!this.rootStore.builderUiStore?.showInvoiceOverlayForId &&
            this.rootStore.builderUiStore?.showInvoiceOverlayForId ===
                this.invoiceContainerBlock?.block_id
        );
    }

    @computed
    get hasChargeBlock() {
        return !!this.chargeBlock;
    }

    @computed
    get hasPaidPayments() {
        return this.invoiceContainerBlock?.hasPaidPayments;
    }

    @computed
    get hasPaidPaymentsWithoutPendingMAP() {
        return this.paymentScheduleBlock?.hasPaidPaymentsWithoutPendingMAP;
    }

    @computed
    get lastPendingPayment() {
        return this.revision.lastPendingPayment;
    }

    @computed
    get hasManualPaidPayments() {
        return this.invoiceContainerBlock?.hasManualPaidPayments;
    }

    @computed
    get overduePayments() {
        return this.revision.overduePayments;
    }

    @computed
    get serviceSelectionBlocks() {
        return this.blocks.filter(
            b => b?.isServiceSelectionBlock
        ) as ServiceSelectionBlock[];
    }

    @computed
    get serviceItemBlocks() {
        return this.blocks.filter(
            b => b.isServiceItemBlock
        ) as ServiceItemBlock[];
    }

    @computed
    get textBlocks() {
        return this.blocks.filter(b => b.isTextBlock) as TextBlock[];
    }

    @computed
    get hasSelection() {
        return this.serviceSelectionBlocks.some(block => block.hasSelection);
    }

    @computed
    get autoSelectedServicesBlocks() {
        return this.serviceSelectionBlocks
            .filter(block => !block.hasSelection && block.send_info_to_invoice)
            .flatMap(
                serviceSelectionBlock =>
                    serviceSelectionBlock.selectedServiceBlocks
            ) as ServiceSelectionItemBlock[];
    }

    @computed
    get clientSelectedServicesBlocks() {
        return this.serviceSelectionBlocks
            .filter(block => block.hasSelection)
            .flatMap(
                serviceSelectionBlock =>
                    serviceSelectionBlock.selectedServiceBlocks
            ) as ServiceSelectionItemBlock[];
    }

    @computed
    get hasServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.length > 0;
    }

    @computed
    get hasChargeableServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.some(
            serviceSelectionBlock => serviceSelectionBlock.isChargeable
        );
    }

    @computed
    get chargeableServiceSelectionBlocks() {
        return this.serviceSelectionBlocks.filter(
            serviceSelectionBlock => serviceSelectionBlock.isChargeable
        ) as ServiceSelectionBlock[];
    }

    @computed
    get chargeableDigitalProductServices() {
        return this.chargeableServiceSelectionBlocks.flatMap(
            serviceSelectionBlock =>
                serviceSelectionBlock.digitalProductServiceItems
        );
    }

    @computed
    get payContainerBlock() {
        return this.blocks.find(b => b.isPayContainerBlock);
    }

    @computed
    get paymentScheduleBlock() {
        return this.blocks.find(
            b => b.isPaymentScheduleBlock
        ) as PaymentScheduleBlock;
    }

    @computed
    get recurringScheduleBlock() {
        return this.blocks.find(
            b => b.isRecurringScheduleBlock
        ) as RecurringScheduleBlock;
    }

    @computed
    get paymentsBlocks() {
        return this.revision.paymentsBlocks;
    }

    @computed
    get hasQuestionBlocks() {
        return this.blocks.filter(b => b.isQuestionBlock()).length > 1;
    }

    @computed
    get chargeContainerBlock() {
        return this.blocks.find(
            b => b.isChargeContainerBlock
        ) as ChargeContainerBlock;
    }

    @computed
    get chargeBlock() {
        return this.blocks.find(b => b.isChargeBlock) as ChargeBlock;
    }

    @computed
    get servicesBlock() {
        return this.blocks.find(b => b.isServicesBlock) as ServicesBlock;
    }

    @computed
    get contractBlocks(): ContractBlock[] {
        return this.blocks.filter(b => b.isContractBlock) as ContractBlock[];
    }

    get contractBlocksWithRecaps() {
        return this.contractBlocks.filter(contract => contract.hasRecaps);
    }

    @computed
    get hasAnySignedContractWithRecaps() {
        return this.contractBlocksWithRecaps.some(
            contract => contract.hasClientSignatures
        );
    }

    get contractBlocksWithServicesRecaps() {
        return this.contractBlocks.filter(
            contract => contract.hasServicesRecaps
        );
    }

    @computed
    get firstContractWithServicesRecapsPage() {
        const contractWithServicesRecapsPages =
            this.contractBlocksWithServicesRecaps.map(
                contract => contract.page
            );
        return first(
            contractWithServicesRecapsPages.sort(
                (pageA, pageB) => pageA.order - pageB.order
            )
        );
    }

    @computed
    get contractHtmlBlocks() {
        return this.blocks.filter(
            b => b.isContractHtmlBlock
        ) as ContractHTMLBlock[];
    }

    @computed
    get questionBlocks() {
        return this.blocks.filter(b => b.isQuestionBlock()) as QuestionBlock[];
    }

    @computed
    get pageWithInvoice() {
        return this.pages.find(p => !!p.invoiceContainerBlock);
    }

    @computed
    get pageWithContract() {
        return this.pages.find(p => !!p.contractBlock);
    }

    @computed
    get pageWithScheduler() {
        return this.pages.find(p => !!p.scheduleContainerBlock);
    }

    @computed
    get pageWithSessionSelection() {
        return this.pages.find(page => !!page.sessionSelectionBlock);
    }

    @computed
    get pagesWithServiceSelection() {
        return this.pages.filter(p => !isEmpty(p.serviceSelectionBlocks));
    }

    @computed
    get pagesWithContract() {
        return this.pages.filter(page => !!page.contractBlock);
    }

    @computed
    get sortedPagesWithServiceSelection() {
        // Add slice function to return a copy of the original array
        return this.pagesWithServiceSelection
            ?.slice()
            .sort((pA: Page, pB: Page) => pA.order - pB.order);
    }

    @computed
    get firstContractPage() {
        return this.pagesWithContract.length > 1
            ? this.pagesWithContract.reduce((a, b) =>
                  a.order < b.order ? a : b
              )
            : this.pagesWithContract[0];
    }

    @computed
    get pagesWithSelectableServices() {
        return this.pages.filter(p => p.hasSelectableServiceSelectionBlocks);
    }

    @computed
    get pageWithPay() {
        return this.pages.find(p => !!p.payContainerBlock);
    }

    get company() {
        return this.rootStore.companyStore?.currCompany;
    }

    @computed
    get draftAndActiveAreEqual() {
        return (
            this.draftRevision?._id === 'draft' ||
            this.draftRevision?._id === this.activeRevision?._id
        );
    }

    @computed
    get currentStep() {
        return window.location.pathname.split('/').slice(4).join('/');
    }

    get flowRevision() {
        return this.draftRevision || this.activeRevision;
    }

    // TODO: Remove once OneComposer is rolled out.
    @computed
    get logoForEmail() {
        return (
            (this.company.hasCompanyLogo &&
                this.company.logo_image?.original_url) ||
            (this.company.hasCompanyIcon && this.company.icon_image?.url) ||
            null
        );
    }

    @computed
    get isDraftWithManualPaidPayments() {
        return this.hasManualPaidPayments && !this.is_published;
    }

    @computed
    get lastPage() {
        return this.flowRevision.pagesMap[this.flowRevision.lastPageId];
    }

    @computed
    get sessionSelectionBlock() {
        return this.serviceSelectionBlocks.find(
            block => block.isSessionSelection
        );
    }

    @computed
    get hasSessionSelectionBlock() {
        return !!this.sessionSelectionBlock;
    }

    @computed
    get flowUrl() {
        return this.flow_url;
    }

    removeAllRecapsBeforeSelection() {
        this.contractBlocks
            .filter(contract => contract.isBeforeSelection)
            .forEach(contract =>
                deleteContractRecaps(
                    contract.contractHTMLBlock,
                    this.rootStore.blockCtrl
                )
            );
    }

    getCustomFields() {
        if (this.is_template || this.isLeadForm) {
            return (
                this.company?.custom_project_fields_v2 ||
                this.company?.custom_project_fields ||
                []
            );
        } else {
            return (
                this.project?.custom_project_fields_v2 ||
                this.project?.custom_project_fields ||
                []
            );
        }
    }

    containsLinkedQuestionType(questionTypeKey) {
        return this.linkedFields.indexOf(questionTypeKey) !== -1;
    }

    getPageBeforePage(pageId: string) {
        const page = this.flowRevision.pagesMap[pageId];
        const beforePageIndex = this.pages.indexOf(page) - 1;

        if (beforePageIndex === -1) {
            return null;
        }

        return this.pages[beforePageIndex];
    }
    @action
    setDefaultEmailTemplate(emailTemplateId: string) {
        this.configuration.default_email_template_id = emailTemplateId;
    }

    @action
    setRequireAuthentication(requireAuthentication: boolean) {
        this.configuration.require_authentication = requireAuthentication;
    }

    @action
    updateFlowFolder(folderId: string, path: string) {
        this.folder_id = folderId;
        this.path = path;
    }

    @action
    setFlowStarred(starred: boolean) {
        this.starred = starred;
    }

    @action
    setLastActionNumber(lastActionNumber: number) {
        this.last_action_number = lastActionNumber;
    }

    @action.bound
    disposeReactions() {
        this.reactions?.forEach(disposer => disposer());
        this.reactions = [];
    }

    @action
    setWasMarkedAsPaidInCurrentSession() {
        this.wasMarkedAsPaidInCurrentSession = true;
    }

    @action
    setLinkedAutomations(linkedAutomations: IWorkflowResponse[]) {
        this.linkedAutomations = linkedAutomations;
    }

    convertToFlowResponse(): IFixedFlowResponse {
        const { rootStore, ...rest } = this;

        return toJS<IFixedFlowResponse>({
            ...rest,
            variables: this.variables,
            pages: this.pages,
            theme: this.theme,
            blocks: this.blocks as TBlockResponse[], // TODO: remove type assertion, has type issues with block type
            pages_info: this.pages.map(page => ({
                page_id: page.page_id,
                is_submitted: !!page.submitted_on,
                submissions: page.submissions,
                submitted_on: page.submitted_on,
                views: page.views,
                was_viewed: page.views.length > 0
            }))
        });
    }

    @action
    setFlowPermissions(flow_permissions: IFlowPermission[]) {
        this.flow_permissions = flow_permissions;
    }

    @action
    setRequireStrongAuth(require_strong_auth: boolean) {
        this.require_strong_auth = require_strong_auth;
    }
}
